import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { cx } from "@/lib/cx.js";

/*
 * Variants map onto the .btn* classes in index.css:
 *
 *   default      .btn                 bordered, neutral
 *   primary      .btn-primary         solid brand — the one committing action
 *   gradient     .btn-gradient        brand→violet, only for auth submits
 *   danger       .btn-danger          solid rose — destructive confirmation
 *   dangerSoft   .btn-danger-soft     outlined rose — destructive entry point
 *   link         .btn-link            inline text action
 *   icon         .btn-icon            square, icon-only (table action columns)
 *
 * `loading` shows a spinner, disables the button and optionally swaps the label —
 * one place instead of the markup's per-page setSubmitting().
 */

const VARIANTS = {
    default: "btn",
    primary: "btn btn-primary",
    gradient: "btn btn-gradient",
    danger: "btn btn-danger",
    dangerSoft: "btn btn-danger-soft",
    link: "btn-link",
    icon: "btn-icon",
};

function Spinner({ className = "size-4" }) {
    return (
        <svg
            className={cx("animate-spin", className)}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            aria-hidden="true"
        >
            <path d="M21 12a9 9 0 1 1-6.22-8.56" />
        </svg>
    );
}

const Button = forwardRef(function Button(
    {
        variant = "default",
        size,
        loading = false,
        loadingLabel,
        disabled,
        to,
        href,
        icon,
        className,
        children,
        type = "button",
        ...rest
    },
    ref,
) {
    const classes = cx(VARIANTS[variant] ?? VARIANTS.default, size === "lg" && "btn-lg", className);

    const content = (
        <>
            {loading ? <Spinner /> : icon}
            {loading && loadingLabel ? loadingLabel : children}
        </>
    );

    // A button that navigates is a link, so it gets middle-click, "open in new
    // tab" and the right role for free.
    if (to) {
        return (
            <Link ref={ref} to={to} className={classes} {...rest}>
                {content}
            </Link>
        );
    }
    if (href) {
        return (
            <a ref={ref} href={href} className={classes} {...rest}>
                {content}
            </a>
        );
    }

    return (
        <button
            ref={ref}
            type={type}
            className={classes}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            {...rest}
        >
            {content}
        </button>
    );
});

export default Button;
export { Spinner };
